import type {
  ChatCompletionContentPart,
  ChatCompletionContentPartRefusal,
  ChatCompletionContentPartText,
  ChatCompletionMessageParam,
} from 'openai/resources';

import { functionToolCalls } from './function_tool_calls';

export type RawApiMessage = ChatCompletionMessageParam;

export type Message = UserMessage | AssistantMessage | ToolMessage;

export interface TextContent {
  type: 'text';
  text: string;
}

export interface UserMessage {
  role: 'user';
  content: TextContent[];
}

export interface AssistantMessage {
  role: 'assistant';
  content: TextContent[];
  tool_calls?: ToolCall[];
}

export interface ToolMessage {
  role: 'tool';
  content: TextContent[];
  tool_call_id: string;
}

export interface ToolCall {
  id: string;
  name: string;
  arguments: string;
  completed: boolean;
}

export function toMessages(
  messages: RawApiMessage[],
  completedToolCallIds: ReadonlySet<string>,
): Message[] {
  const allCompletedToolCallIds = new Set([
    ...completedToolCallIds,
    ...messages.flatMap((m) => (m.role === 'tool' ? [m.tool_call_id] : [])),
  ]);

  return messages
    .filter((m) => m.role !== 'system' && m.role !== 'developer')
    .flatMap((message) => {
      const converted = toMessage(message, allCompletedToolCallIds);
      return converted ? [converted] : [];
    });
}

function toMessage(
  message: RawApiMessage,
  completedToolCallIds: ReadonlySet<string>,
): Message | undefined {
  if (message.role === 'user') {
    return {
      role: 'user',
      content: toTextContent(message.content),
    };
  }

  if (message.role === 'assistant') {
    const tool_calls = parseToolCalls(message, completedToolCallIds);

    return {
      role: 'assistant',
      content: toTextContent(message.content),

      ...(tool_calls !== undefined && { tool_calls }),
    };
  }

  if (message.role === 'tool') {
    return {
      role: 'tool',
      content: toTextContent(message.content),
      tool_call_id: message.tool_call_id,
    };
  }
}

type ContentPart =
  | ChatCompletionContentPart
  | ChatCompletionContentPartRefusal
  | ChatCompletionContentPartText;

function toTextContent(
  content: string | readonly ContentPart[] | null | undefined,
): TextContent[] {
  if (typeof content === 'string') {
    return content === '' ? [] : [{ type: 'text', text: content }];
  }

  return content?.filter((part) => part.type === 'text') ?? [];
}

function parseToolCalls(
  message: Extract<RawApiMessage, { role: 'assistant' }>,
  completedToolCallIds: ReadonlySet<string>,
): ToolCall[] | undefined {
  if (!message.tool_calls) return;

  return functionToolCalls(message).map((tc) => ({
    id: tc.id,
    name: tc.function.name,
    arguments: tc.function.arguments,
    completed: completedToolCallIds.has(tc.id),
  }));
}
